import { CalendarToday, RemoveRedEye, StarBorderRounded, StarRounded } from '@mui/icons-material';
import { Divider, Stack, Tooltip } from '@mui/material';
import Typography from 'component/MarketPlace/Typography';
import React from 'react';
import Highlighter from 'react-highlight-words';
import GlobalSearchCard from '../GlobalSearchCard';
import { StyledRating } from './knowledgeCard.style';
import { convertToShortNumber, getKbDate } from 'utils/functions';
import KnowledgeCardSkeleton from './skeleton';

interface Knowledge {
  id: string;
  name: string;
  knowledgeBase: string;
  number: string;
  content: string;
  rating: number;
  score: number;
  viewCount: number;
  publishedDate: string;
  lastUpdated?: string;
}

export interface KnowledgeCardProps {
  item: Knowledge;
  keywords?: string[];
  onClickViewDetail?: () => void;
  locale?: Record<string, any>;
  isShowPublishedDate?: boolean;
  standalone?: boolean;
  viewCountConfig?: {
    isConvertViewCountToShortNumber?: boolean;
    isShowViewCountTooltip?: boolean; 
    isShowViewCountShortNumberSpacing?: boolean;
  };
  dateFormat: string;
  timeZone: string;
}

const KnowledgeCard = (props: KnowledgeCardProps) => {
  const { item, keywords = [], onClickViewDetail, locale, isShowPublishedDate, viewCountConfig = {}, standalone, dateFormat, timeZone } = props;
  const { isConvertViewCountToShortNumber, isShowViewCountTooltip, isShowViewCountShortNumberSpacing } = viewCountConfig;

  const mapBreadcrumb = () => {
    const pathArray = ['home', 'knowledge articles'];
    return pathArray.map((path) => locale?.breadcrumb?.[path] || path).join(' / ');
  };

  return (
    <GlobalSearchCard
      title={
        <Highlighter
          highlightClassName="activeHighlight"
          searchWords={keywords}
          textToHighlight={item?.name}
          autoEscape
        />
      }
      titleText={item?.name}
      standalone={standalone}
      description={item?.content}
      breadcrumbs={mapBreadcrumb()}
      onClickViewDetail={onClickViewDetail}
      locale={locale}
      paperProps={{ 'data-kb_number': item?.number }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing="16px"
        mt="36px"
        mb="-8px"
      >
        <Stack direction="row" alignItems="center">
          <CalendarToday fontSize="small" sx={{ color: 'action.active', width: { xs: '14px', md: '1em'}}} />
          <Typography variant="body2" color="text.secondary" ml="8px" sx={{fontSize: {xs: '10px', md: '14px'}}}>
            {getKbDate(item, !!isShowPublishedDate, dateFormat, timeZone)}
          </Typography>
        </Stack>

        <Divider orientation="vertical" variant="middle" flexItem />

        <Stack aria-label={item.rating > 1 ? `${item.rating} stars` : `${item.rating} star`} role="img">
          <StyledRating
            value={item.rating}
            precision={0.5}
            readOnly
            icon={<StarRounded sx={{fontSize: {xs: '14px', md: '1em'}}} />}
            emptyIcon={<StarBorderRounded sx={{fontSize: {xs: '14px', md: '1em'}}} />}
          />
        </Stack>

        <Divider orientation="vertical" variant="middle" flexItem />

        <Stack direction="row" alignItems="center">
          <RemoveRedEye fontSize="small" sx={{ color: 'action.active', width: { xs: '14px', md: '1em'} }} />
          <Tooltip title={isShowViewCountTooltip ? item.viewCount : ""} placement="top-start">
            <Typography variant="body2" color="text.secondary" ml="8px" sx={{fontSize: {xs: '10px', md: '14px'}}}>
              {isConvertViewCountToShortNumber ? convertToShortNumber(item.viewCount, { spacing: isShowViewCountShortNumberSpacing }) : item.viewCount}
            </Typography>
          </Tooltip>
        </Stack>
      </Stack>
    </GlobalSearchCard>
  );
};

KnowledgeCard.Skeleton = KnowledgeCardSkeleton;

export default KnowledgeCard;
